import { type ReactElement } from "react";

export interface ControlFieldsState {
  lots: number;
  initialSl: number;
  trailTrigger: number;
  trailAmount: number;
  /** Rung-1 cost buffer (2026-07-15 ladder rework). */
  trailSlippage: number;
  windowStart: string;
  windowEnd: string;
  dedupSeconds: number;
  /** LIVE swing dimensions (2026-08-06): the swing the position books
   * at, and the entry sweep's own. Shadow is no longer configurable. */
  targetPivot: number;
  sweepPivot: number;
}

interface NumberRowProps {
  state: ControlFieldsState;
  onLots: (v: number) => void;
  onTrailTrigger: (v: number) => void;
  onTrailAmount: (v: number) => void;
  onTrailSlippage: (v: number) => void;
  /** Persist the current Lots value as this strategy's own default —
   * what the morning auto-seed job writes each fresh trading day.
   * Omitted while the save is in flight (button hides its "saving" state
   * via the loading param instead of disabling the whole row). */
  onSaveLotsAsDefault?: () => void;
  savingLotsDefault?: boolean;
  /** False for bias, which stopped trailing on 2026-08-06 — fixed stop
   * plus a swing target. Showing a ladder it cannot run is worse than
   * showing nothing. */
  trails?: boolean;
}

export function NumberRow({
  state,
  onLots,
  onTrailTrigger,
  onTrailAmount,
  onTrailSlippage,
  onSaveLotsAsDefault,
  savingLotsDefault,
  trails = true,
}: Readonly<NumberRowProps>): ReactElement {
  return (
    <div className={`mb-3 grid grid-cols-2 gap-3 ${trails ? "md:grid-cols-4" : "md:grid-cols-1"}`}>
      <NumberField
        label="Lots"
        value={state.lots}
        onChange={onLots}
        onSaveAsDefault={onSaveLotsAsDefault}
        savingDefault={savingLotsDefault}
      />
      {trails && (
        <>
          <NumberField label="Trail trigger" value={state.trailTrigger} onChange={onTrailTrigger} />
          <NumberField label="Trail amount" value={state.trailAmount} onChange={onTrailAmount} />
          <NumberField
            label="Trail buffer"
            value={state.trailSlippage}
            onChange={onTrailSlippage}
          />
        </>
      )}
    </div>
  );
}

interface WindowRowProps {
  state: ControlFieldsState;
  onWindowStart: (v: string) => void;
  onWindowEnd: (v: string) => void;
  onDedupSeconds: (v: number) => void;
}

export function WindowRow({
  state,
  onWindowStart,
  onWindowEnd,
  onDedupSeconds,
}: Readonly<WindowRowProps>): ReactElement {
  return (
    <div className="mb-3 grid grid-cols-3 gap-3">
      <TimeField label="Window start" value={state.windowStart} onChange={onWindowStart} />
      <TimeField label="Window end" value={state.windowEnd} onChange={onWindowEnd} />
      <NumberField label="Dedup sec" value={state.dedupSeconds} onChange={onDedupSeconds} />
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  /** Optional "Save as default" affordance (TradingView-style) — only
   * the Lots field uses this today. */
  onSaveAsDefault?: (() => void) | undefined;
  savingDefault?: boolean | undefined;
}

function NumberField({
  label,
  value,
  onChange,
  onSaveAsDefault,
  savingDefault,
}: Readonly<NumberFieldProps>): ReactElement {
  return (
    <div>
      <label className="block">
        <span className="label">{label}</span>
        <input
          type="number"
          className="input"
          value={value}
          onChange={(e) => {
            const next = Number.parseInt(e.target.value, 10);
            onChange(Number.isFinite(next) ? next : 0);
          }}
        />
      </label>
      {onSaveAsDefault !== undefined && (
        <button
          type="button"
          className="mt-1 text-[11px] text-accent hover:underline disabled:opacity-50"
          disabled={savingDefault === true}
          onClick={onSaveAsDefault}
        >
          {savingDefault === true ? "Saving…" : "Save as default"}
        </button>
      )}
    </div>
  );
}

interface TimeFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
}

function TimeField({ label, value, onChange }: Readonly<TimeFieldProps>): ReactElement {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <input
        type="time"
        className="input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </label>
  );
}

interface TrailPreviewProps {
  initialSl: number;
  trailTrigger: number;
  trailAmount: number;
  trailSlippage: number;
}

/** Rungs 1.._STANDARD_RUNGS gain the trail amount per rung; beyond that
 * the ladder goes 1:1 (trigger per rung). Mirrors the backend's
 * trail_ladder._STANDARD_RUNGS (2026-07-15 rework). */
const STANDARD_RUNGS = 5;

/** 6-rung ladder preview anchored at a hypothetical entry of Rs 165 —
 * rung 1 = entry + buffer, +amount through rung 5, 1:1 from rung 6. */
export function TrailPreview({
  initialSl,
  trailTrigger,
  trailAmount,
  trailSlippage,
}: Readonly<TrailPreviewProps>): ReactElement {
  const ENTRY = 165;
  const steps: number[] = [ENTRY - initialSl];
  for (let rung = 1; rung <= 6; rung++) {
    const standardRungs = Math.min(rung, STANDARD_RUNGS);
    steps.push(
      ENTRY +
        trailSlippage +
        trailAmount * (standardRungs - 1) +
        trailTrigger * Math.max(rung - STANDARD_RUNGS, 0),
    );
  }
  const firstSl = String(steps[0]);
  const ladder = steps
    .slice(1)
    .map((s) => `Rs ${String(s)}`)
    .join(" -> ");
  return (
    <div className="rounded-ctl bg-surface-2 p-3 text-xs text-ink-secondary">
      <span className="font-medium text-ink">Trail preview:</span> entry Rs {String(ENTRY)}, initial
      SL Rs {firstSl} -&gt; {ladder} ...
    </div>
  );
}
